import { useEffect } from "react";
import { useLocation } from "@/lib/router-compat";
import { maybeTrackViewContent } from "@/lib/conversions";

/**
 * Scrolls to the top of the page on every route change.
 * React Router v6 preserves scroll position by default, which causes
 * users to land mid-page (or at the bottom) when clicking internal links.
 * This component resets scroll to the top whenever the pathname changes.
 *
 * Also fires a GA4 page_view event on every client-side navigation so the
 * SPA's virtual page changes are tracked by Google Analytics 4, and fires
 * a Facebook Pixel ViewContent event on key SAINT landing pages.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });

    // Fire GA4 page_view for SPA route changes. Queue onto dataLayer so
    // the event still fires if gtag hasn't loaded yet (deferred after load).
    if (typeof window !== "undefined") {
      const w = window as any;
      const payload = {
        page_location: window.location.href,
        page_title: document.title,
        page_path: window.location.pathname,
      };
      // window.gtag is defined in injectGtag() before gtag.js loads and
      // pushes `arguments` onto dataLayer, which gtag.js drains on init.
      // Never push a plain array — gtag.js ignores those entries.
      w.gtag =
        w.gtag ||
        function () {
          (w.dataLayer = w.dataLayer || []).push(arguments);
        };
      w.gtag("event", "page_view", payload);
    }

    // Fire Facebook Pixel ViewContent on key SAINT pages
    maybeTrackViewContent(pathname);
  }, [pathname]);

  return null;
};

export default ScrollToTop;
